import { Component, computed, input, output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DocumentStorageUsageModel } from '../../../../core/models/data/document.model';
import { DocumentUtilities } from '../../../../core/utilities/document.utilities';

/** One coloured slice of the usage bar, reused to render both the bar segment and its legend entry. */
interface StorageSegment {
    key: 'used' | 'deleted' | 'free';
    label: string;
    valueLabel: string;
    widthPercent: number;
    barClass: string;
    swatchClass: string;
}

/**
 * Presentational Document Center storage usage bar graph (DEV-2426): a horizontal, Windows
 * Explorer-style segmented bar with a legend and per-segment totals for Used, Trash, and
 * Free space. The page owns the data fetch (and the documents.manage permission gate) and feeds the
 * model in; an unlimited storage (`allottedBytes` null) renders the Used/Recently-Deleted split with
 * Free shown as "Unlimited".
 */
@Component({
    selector: 'ms-document-storage-usage',
    standalone: true,
    imports: [FontAwesomeModule],
    templateUrl: './document-storage-usage.component.html',
    styleUrl: './document-storage-usage.component.scss'
})
export class DocumentStorageUsageComponent {

    public usage = input<DocumentStorageUsageModel | undefined>(undefined);
    public isLoading = input<boolean>(false);

    // Raised when the user asks to permanently empty the Trash. The page owns the confirmation modal
    // and the hard-delete call (and the documents.manage gate that already governs this bar).
    public clearTrashRequested = output<void>();

    public DocumentsMessagingConstants = DocumentsMessagingConstants;

    // Unlimited storage: no denominator/total. The API returns null allotted/free in this case.
    public isUnlimited = computed<boolean>(() => this.usage()?.allottedBytes == null);

    // Whether the Trash holds anything to purge. The Empty Trash button always renders but is disabled
    // when this is false — an empty Trash has nothing to reclaim, so the action only enables once a
    // soft-delete puts bytes there.
    public hasTrash = computed<boolean>(() => (this.usage()?.deletedBytes ?? 0) > 0);

    public segments = computed<StorageSegment[]>(() => {
        const usage = this.usage();
        if (!usage) return [];

        const used = usage.activeBytes;
        const deleted = usage.deletedBytes;
        const unlimited = this.isUnlimited();

        // Bounded → scale every segment against the allotted total (Used + Deleted + Free = allotted).
        // Unlimited → there is no total, so scale Used/Deleted against their own sum to show the split.
        const denominator = unlimited ? used + deleted : (usage.allottedBytes ?? 0);

        return [
            {
                key: 'used',
                label: DocumentsMessagingConstants.DOCUMENTS_STORAGE_USED_LABEL(),
                valueLabel: DocumentUtilities.formatBytes(used),
                widthPercent: this.toPercent(used, denominator),
                barClass: 'storage-bar-used',
                swatchClass: 'swatch-used'
            },
            {
                key: 'deleted',
                label: DocumentsMessagingConstants.DOCUMENTS_STORAGE_DELETED_LABEL(),
                valueLabel: DocumentUtilities.formatBytes(deleted),
                widthPercent: this.toPercent(deleted, denominator),
                barClass: 'storage-bar-deleted',
                swatchClass: 'swatch-deleted'
            },
            {
                key: 'free',
                label: DocumentsMessagingConstants.DOCUMENTS_STORAGE_FREE_LABEL(),
                valueLabel: unlimited
                    ? DocumentsMessagingConstants.DOCUMENTS_STORAGE_UNLIMITED()
                    : DocumentUtilities.formatBytes(usage.freeBytes ?? 0),
                widthPercent: unlimited ? 0 : this.toPercent(usage.freeBytes ?? 0, denominator),
                barClass: 'storage-bar-free',
                swatchClass: 'swatch-free'
            }
        ];
    });

    public summaryLabel = computed<string>(() => {
        const usage = this.usage();
        if (!usage) return '';
        // Trash (recently-deleted) still occupies the storage, so the headline "used" total counts it
        // alongside active files — matching how it eats into the allotted total on the bar.
        const used = DocumentUtilities.formatBytes(usage.activeBytes + usage.deletedBytes);
        if (this.isUnlimited()) {
            return DocumentsMessagingConstants.DOCUMENTS_STORAGE_SUMMARY_UNLIMITED().replaceValuesWithObject({ used });
        }
        return DocumentsMessagingConstants.DOCUMENTS_STORAGE_SUMMARY().replaceValuesWithObject({
            used,
            total: DocumentUtilities.formatBytes(usage.allottedBytes ?? 0)
        });
    });

    // Low-storage warning: only meaningful for a bounded quota — unlimited storage never warns. True
    // when free space sits at or below the configured threshold percent of the allotted total.
    public isLowOnStorage = computed<boolean>(() => {
        const usage = this.usage();
        if (!usage || this.isUnlimited()) return false;
        const allotted = usage.allottedBytes ?? 0;
        if (allotted <= 0) return false;
        const freeFraction = (usage.freeBytes ?? 0) / allotted;
        return freeFraction <= DocumentsConstants.LOW_STORAGE_WARNING_THRESHOLD_PERCENT() / 100;
    });

    public lowStorageMessage = computed<string>(() => {
        const usage = this.usage();
        if (!usage) return '';
        const allotted = usage.allottedBytes ?? 0;
        const free = usage.freeBytes ?? 0;
        const percent = allotted > 0 ? Math.round((free / allotted) * 100) : 0;
        return DocumentsMessagingConstants.DOCUMENTS_STORAGE_LOW_WARNING().replaceValuesWithObject({
            free: DocumentUtilities.formatBytes(free),
            percent
        });
    });

    public ariaLabel = computed<string>(() => {
        const usage = this.usage();
        if (!usage) return '';
        const values = {
            used: DocumentUtilities.formatBytes(usage.activeBytes),
            deleted: DocumentUtilities.formatBytes(usage.deletedBytes)
        };
        if (this.isUnlimited()) {
            return DocumentsMessagingConstants.DOCUMENTS_STORAGE_ARIA_UNLIMITED().replaceValuesWithObject(values);
        }
        return DocumentsMessagingConstants.DOCUMENTS_STORAGE_ARIA().replaceValuesWithObject({
            ...values,
            free: DocumentUtilities.formatBytes(usage.freeBytes ?? 0),
            total: DocumentUtilities.formatBytes(usage.allottedBytes ?? 0)
        });
    });

    // Share of the bar for one segment. A zero/absent denominator yields 0% (an empty track) rather
    // than NaN. Widths are kept at full precision so a bounded limit's segments sum to exactly 100%.
    private toPercent(value: number, denominator: number): number {
        if (!denominator || denominator <= 0) return 0;
        return (value / denominator) * 100;
    }
}
